import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../Api';
import { TrustDto } from '../types/Trust';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { TRUST_TYPES, TRUST_MEMBER_TYPES } from '../constants/trust';
import { FormSelect } from '../controls/FormSelect';
import CustomInput from '../controls/CustomInput';
import { formatTaxId, unformatTaxId } from '../utils/formatting';
import { tin } from '../constants/regex';

const TrustFormPage: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  
  const [formData, setFormData] = useState<TrustDto>({
    id: 0,
    name: '',
    trustType: '',
    trustJurisdiction: '',
    tin: '',
    dateOfFormation: '',
    individualId: 0,
    trustMembers: []
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const loadTrust = async (trustId: number) => {
      try {
        setIsLoading(true);
        const response = await api.get(`/api/trust/gettrust?trustId=${trustId}`);
        const trust = response.data.trust;
        
        // Format the TIN for display
        setFormData({
          ...trust,
          tin: formatTaxId(trust.tin)
        });
      } catch (error) {
        console.error('Error loading trust:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Check if we're editing an existing trust
    const trustId = new URLSearchParams(location.search).get('trustId');
    if (trustId) {
      setIsEditing(true);
      loadTrust(parseInt(trustId));
    }
  }, [location.search, api]);

  const handleTrustMemberChange = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      trustMembers: (prev.trustMembers || []).map((member, i) => 
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  const addTrustMember = () => {
    setFormData(prev => ({
      ...prev,
      trustMembers: [
        ...(prev.trustMembers || []),
        {
          id: 0,
          type: '',
          companyWebsite: '',
          email: '',
          phoneNumber: '',
          contactNotes: '',
          trustId: prev.id
        }
      ]
    }));
  };

  const removeTrustMember = (index: number) => {
    setFormData(prev => ({
      ...prev,
      trustMembers: (prev.trustMembers || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!familyId) return;

    try {
      setIsLoading(true);
      
      // Prepare data with unformatted TIN
      const submitData = {
        ...formData,
        tin: unformatTaxId(formData.tin)
      };
      
      if (isEditing) {
        await api.put(`/api/trust/updatetrust?familyId=${familyId}`, submitData);
      } else {
        await api.post(`/api/trust/createtrust?familyId=${familyId}`, submitData);
      }
      
      navigate(`/family-management/${familyId}`);
    } catch (error) {
      console.error('Error saving trust:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (familyId) {
      navigate(`/family-management/${familyId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">
                {isEditing ? 'Edit Trust' : 'Add New Trust'}
              </h1>
              <p className="text-lg">
                {isEditing ? 'Update trust information' : 'Enter trust information to add to the family'}
              </p>
            </div>
          </div>

          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <CustomInput
                  label="Trust Name"
                  name="name"
                  value={formData.name}
                  onChange={(val) => setFormData(prev => ({ ...prev, name: val }))}
                  required
                />
              </div>
              <div>
                <FormSelect
                  label="Trust Type"
                  value={formData.trustType || null}
                  options={Object.entries(TRUST_TYPES).map(([key, value]) => ({ value: key, label: value as string }))}
                  onChange={(val) => setFormData(prev => ({ ...prev, trustType: (val as string) ?? '' }))}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Trust Jurisdiction"
                  name="trustJurisdiction"
                  value={formData.trustJurisdiction}
                  onChange={(val) => setFormData(prev => ({ ...prev, trustJurisdiction: val }))}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="TIN (Tax Identification Number)"
                  name="tin"
                  value={formData.tin}
                  onChange={(val) => setFormData(prev => ({ ...prev, tin: formatTaxId(val) }))}
                  required
                  placeholder="XX-XXXXXXX"
                  pattern={tin.source}
                  title="Please enter a valid TIN in the format XX-XXXXXXX"
                />
              </div>
              <div>
                <CustomInput
                  label="Date of Formation"
                  name="dateOfFormation"
                  type="date"
                  value={formData.dateOfFormation}
                  onChange={(val) => setFormData(prev => ({ ...prev, dateOfFormation: val }))}
                  required
                />
              </div>
            </div>
          </div>

          {/* Trust Members Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Trust Members</h3>
            <div className="space-y-4">
              {(!formData.trustMembers || formData.trustMembers.length === 0) ? (
                <p className="text-gray-500 text-sm">No trust members added yet.</p>
              ) : (
                (formData.trustMembers || []).map((member, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-medium text-gray-800">Trust Member {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeTrustMember(index)}
                        className="btn-danger btn-icon"
                        title="Remove trust member"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <FormSelect
                          label="Type"
                          value={member.type || null}
                          options={Object.entries(TRUST_MEMBER_TYPES).map(([key, value]) => ({ value: key, label: value as string }))}
                          onChange={(val) => handleTrustMemberChange(index, 'type', (val as string) ?? '')}
                          required
                        />
                      </div>
                      <div>
                        <CustomInput
                          label="Company Website"
                          name={`member-companyWebsite-${index}`}
                          value={member.companyWebsite}
                          onChange={(val) => handleTrustMemberChange(index, 'companyWebsite', val)}
                          required
                        />
                      </div>
                      <div>
                        <CustomInput
                          label="Email"
                          name={`member-email-${index}`}
                          type="email"
                          value={member.email}
                          onChange={(val) => handleTrustMemberChange(index, 'email', val)}
                          required
                        />
                      </div>
                      <div>
                        <CustomInput
                          label="Phone Number"
                          name={`member-phoneNumber-${index}`}
                          type="tel"
                          value={member.phoneNumber}
                          onChange={(val) => handleTrustMemberChange(index, 'phoneNumber', val)}
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Notes</label>
                        <textarea
                          value={member.contactNotes || ''}
                          onChange={(e) => handleTrustMemberChange(index, 'contactNotes', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Additional notes about this trust member..."
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={addTrustMember}
                  className="btn-primary"
                >
                  <PlusIcon />
                  Add Trust Member
                </button>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Trust' : 'Add Trust')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TrustFormPage;
